import { useMemo } from 'react'
import { useNavigation } from '../../../context/useNavigation'
import type { ChecklistItem } from '../../../types/timeline'
import type { TimelineElement } from '../../../types/timelineElement'
import { getElementColor } from '../viewUtils'

export type ScopeLevel = 'board' | 'goal' | 'task' | 'subtask'

export interface PaletteChild {
  id: string
  ownColor?: string
  ancestorHue?: number
}

export type ScopedChildKind = 'goal' | 'task' | 'subtask' | 'checklist'

export interface ScopedChild extends PaletteChild {
  element?: TimelineElement
  checklistItem?: ChecklistItem
  kind: ScopedChildKind
  title: string
}

export interface ScopeResolution {
  level: ScopeLevel
  parent: TimelineElement | null
  children: ScopedChild[]
  grandchildren: (childId: string) => ScopedChild[]
  nextLevelName: ScopedChildKind
  drillInto: (childId: string) => void
  drillUp: () => void
}

const asChild = (element: TimelineElement, kind: Exclude<ScopedChildKind, 'checklist'>, _ancestorHue: number, index: number): ScopedChild => ({
  id: element.id,
  element,
  kind,
  title: element.title,
  ownColor: element.color ?? getElementColor(element, index),
})

export function useScopeChildren(elements: readonly TimelineElement[]): ScopeResolution {
  const navigation = useNavigation()
  const { currentLevel, activeGoalId, activeTaskId, activeSubTaskId, goToDashboard, goToGoal, goToTask, goToSubTask } = navigation

  return useMemo(() => {
    const elementsById = new Map(elements.map((element) => [element.id, element]))
    const goal = activeGoalId ? elementsById.get(activeGoalId) ?? null : null
    const task = activeTaskId ? elementsById.get(activeTaskId) ?? null : null
    const subtask = activeSubTaskId ? elementsById.get(activeSubTaskId) ?? null : null
    const childElements = (type: TimelineElement['type'], parentId: string | null, kind: Exclude<ScopedChildKind, 'checklist'>) =>
      elements.filter((element) => element.type === type && element.parentId === parentId)
        .map((element, index) => asChild(element, kind, 0, index))

    const checklistChildren = (parent: TimelineElement | null): ScopedChild[] =>
      (parent?.checklist ?? []).map((item) => ({
        id: item.id,
        checklistItem: item,
        kind: 'checklist',
        title: item.text || '(item)',
      }))

    if (currentLevel === 'goal') {
      const children = childElements('TASK', activeGoalId, 'task')
      return {
        level: 'goal' as const,
        parent: goal,
        children,
        grandchildren: (id) => childElements('SUBTASK', id, 'subtask'),
        nextLevelName: 'task' as const,
        drillInto: (id) => { if (activeGoalId) goToTask(activeGoalId, id) },
        drillUp: goToDashboard,
      }
    }
    if (currentLevel === 'task') {
      const children = childElements('SUBTASK', activeTaskId, 'subtask')
      return {
        level: 'task' as const,
        parent: task,
        children,
        grandchildren: (id) => checklistChildren(elementsById.get(id) ?? null),
        nextLevelName: 'subtask' as const,
        drillInto: (id) => { if (activeGoalId && activeTaskId) goToSubTask(activeGoalId, activeTaskId, id) },
        drillUp: () => { if (activeGoalId) goToGoal(activeGoalId) },
      }
    }
    if (currentLevel === 'subtask') {
      return {
        level: 'subtask' as const,
        parent: subtask,
        children: checklistChildren(subtask),
        grandchildren: () => [],
        nextLevelName: 'checklist' as const,
        drillInto: () => {},
        drillUp: () => { if (activeGoalId && activeTaskId) goToTask(activeGoalId, activeTaskId) },
      }
    }
    const children = elements.filter((element) => element.type === 'GOAL')
      .map((element, index) => asChild(element, 'goal', 0, index))
    return {
      level: 'board' as const,
      parent: null,
      children,
      grandchildren: (id) => {
        return childElements('TASK', id, 'task')
      },
      nextLevelName: 'goal' as const,
      drillInto: goToGoal,
      drillUp: () => {},
    }
  }, [elements, currentLevel, activeGoalId, activeTaskId, activeSubTaskId, goToDashboard, goToGoal, goToTask, goToSubTask])
}
